import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Loader2, Mail, KeyRound, Ban, RotateCcw, GraduationCap, Trophy, Flame,
  CheckCircle2, XCircle, Clock, FileText, ExternalLink, ClipboardList, Download,
  Pencil, Save, X, History, ArrowRight, Eye, EyeOff, Copy, Ticket, KeyRound as PwdIcon, Repeat,
  ChevronDown, Search, Check, Plus, Building2, Layers,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  getPartnerStudentDetail, updateStudentDetails, getStudentChangeLog, enableProgramChange,
  getPublicUniversities, getPublicColleges, getPublicCourseGroups, getPublicCourses,
} from '@/api/stnApi';

// Fields the partner may edit, mapped to the `application` keys returned by the API.
const EDIT_FIELDS = [
  ['fullName', 'Full name'], ['email', 'Email'], ['phone', 'Phone'],
  ['fatherName', "Father's name"], ['motherName', "Mother's name"],
  ['university', 'University'], ['college', 'College'], ['courseGroup', 'Course stream'], ['degree', 'Degree / course'],
  ['session', 'Session'], ['semester', 'Semester'], ['year', 'Year'], ['graduationYear', 'Graduation year'],
  ['universityRegNo', 'University reg. no.'], ['classRollNo', 'Class roll no.'], ['universityRollNo', 'University roll no.'],
  ['location', 'Location'], ['linkedin', 'LinkedIn'], ['github', 'GitHub'], ['portfolio', 'Portfolio'],
];
// These four bind to dynamic, cascading dropdowns (University → College, Stream → Course).
const ACADEMIC_FIELDS = new Set(['university', 'college', 'courseGroup', 'degree']);

// Searchable combobox that also accepts free text (entries not in the list).
function Combobox({ value, onSelect, options, loading, placeholder, icon: Icon, emptyHint, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (s ? options.filter((o) => o.name.toLowerCase().includes(s)) : options).slice(0, 200);
  }, [q, options]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
        className={cn('mt-1 w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm flex items-center gap-2 text-left', disabled && 'opacity-60 cursor-not-allowed')}>
        {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
        <span className={cn('flex-1 truncate', !value && 'text-muted-foreground')}>{value || placeholder}</span>
        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-border sticky top-0 bg-card">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search or type…" className="h-8 pl-7 text-xs" />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
            ) : (
              <>
                {filtered.map((o) => (
                  <button key={o.id ?? o.name} type="button" onClick={() => { onSelect(o); setOpen(false); setQ(''); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-muted/50">
                    <span className="flex-1 truncate">{o.name}{o.state ? <span className="text-muted-foreground"> · {o.state}</span> : ''}</span>
                    {value === o.name && <Check className="w-3.5 h-3.5 text-primary" />}
                  </button>
                ))}
                {q.trim() && !filtered.some((o) => o.name.toLowerCase() === q.trim().toLowerCase()) && (
                  <button type="button" onClick={() => { onSelect({ id: null, name: q.trim() }); setOpen(false); setQ(''); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-muted/50 border-t border-border">
                    <Plus className="w-3 h-3 text-primary" /> <span className="text-primary">Use “{q.trim()}”</span>
                  </button>
                )}
                {!loading && filtered.length === 0 && !q.trim() && (
                  <p className="px-3 py-3 text-xs text-muted-foreground">{emptyHint || 'No options. Type to add your own.'}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const scoreCls = (s) => s >= 75 ? 'text-emerald-500' : s >= 50 ? 'text-amber-500' : 'text-rose-500';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export default function StudentDetailDrawer({ batchId, studentId, onClose, onAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoad] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [logs, setLogs] = useState(null);
  const [showCoupon, setShowCoupon] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success('Copied'); };

  // ── Cascading academic dropdown data (shared with the InternNexus apply form) ──
  const [universities, setUniversities] = useState([]);
  const [colleges, setColleges]         = useState([]);
  const [courseGroups, setCourseGroups] = useState([]);
  const [courses, setCourses]           = useState([]);
  const [uniLoading, setUniLoading]       = useState(false);
  const [colLoading, setColLoading]       = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [selectedUniId, setSelectedUniId]       = useState(null);
  const [selectedGroupCode, setSelectedGroupCode] = useState(null);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load the top-level lists (universities + course streams) once edit mode opens.
  useEffect(() => {
    if (!editing) return;
    if (!universities.length) {
      setUniLoading(true);
      getPublicUniversities().then((r) => setUniversities(r?.universities || [])).catch(() => {}).finally(() => setUniLoading(false));
    }
    if (!courseGroups.length) {
      setGroupsLoading(true);
      getPublicCourseGroups().then((r) => setCourseGroups(r?.courseGroups || [])).catch(() => {}).finally(() => setGroupsLoading(false));
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Match the intern's existing university / stream names back to their ids so the
  // dependent college / course lists load for already-filled values.
  useEffect(() => {
    if (!editing || selectedUniId || !universities.length) return;
    const m = universities.find((u) => u.name?.toLowerCase() === String(form.university || '').toLowerCase());
    if (m) setSelectedUniId(m.id);
  }, [editing, universities]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!editing || selectedGroupCode || !courseGroups.length) return;
    const m = courseGroups.find((g) => g.name?.toLowerCase() === String(form.courseGroup || '').toLowerCase());
    if (m) setSelectedGroupCode(m.code);
  }, [editing, courseGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // University → colleges, stream → courses (cascades).
  useEffect(() => {
    if (!selectedUniId) { setColleges([]); return; }
    setColLoading(true);
    getPublicColleges({ universityId: selectedUniId }).then((r) => setColleges(r?.colleges || [])).catch(() => setColleges([])).finally(() => setColLoading(false));
  }, [selectedUniId]);
  useEffect(() => {
    if (!selectedGroupCode) { setCourses([]); return; }
    setCourseLoading(true);
    getPublicCourses(selectedGroupCode).then((r) => setCourses(r?.courses || [])).catch(() => setCourses([])).finally(() => setCourseLoading(false));
  }, [selectedGroupCode]);

  // Picking a parent clears its dependent field so stale pairings can't persist.
  const pickUniversity = (u) => { setField('university', u.name); setSelectedUniId(u.id || null); setField('college', ''); };
  const pickStream     = (g) => { setField('courseGroup', g.name); setSelectedGroupCode(g.id || null); setField('degree', ''); };

  const reload = () =>
    getPartnerStudentDetail(batchId, studentId)
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Could not load report'));

  useEffect(() => {
    setLoad(true);
    reload().finally(() => setLoad(false));
  }, [batchId, studentId]);

  const st = data?.student;
  const app = data?.application || {};
  const programs = data?.programs || [];

  const startEdit = () => {
    const f = {};
    EDIT_FIELDS.forEach(([k]) => { f[k] = app[k] ?? ''; });
    setForm(f);
    setSelectedUniId(null); setSelectedGroupCode(null); // re-match against prefilled names
    setEditing(true);
  };
  const saveEdits = async () => {
    // Only send fields that actually changed.
    const updates = {};
    EDIT_FIELDS.forEach(([k]) => { if ((form[k] ?? '') !== (app[k] ?? '')) updates[k] = form[k]; });
    if (!Object.keys(updates).length) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await updateStudentDetails(batchId, studentId, updates);
      toast.success(res?.message || 'Saved');
      setEditing(false);
      await reload();
      if (showHistory) loadHistory();
    } catch (e) { toast.error(e?.message || 'Could not save'); }
    finally { setSaving(false); }
  };
  const loadHistory = () => {
    getStudentChangeLog(batchId, studentId).then((r) => setLogs(r?.logs || [])).catch(() => setLogs([]));
  };
  const toggleHistory = () => {
    const next = !showHistory; setShowHistory(next);
    if (next && logs === null) loadHistory();
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Loading report…</div>
        ) : !st ? (
          <p className="py-20 text-center text-muted-foreground">No data.</p>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {st.name}
                {st.revoked
                  ? <Badge variant="outline" className="bg-rose-500/10 text-rose-500 border-rose-500/25 text-[10px]">Paused</Badge>
                  : <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/25 text-[10px]">Active</Badge>}
              </SheetTitle>
            </SheetHeader>
            <p className="text-xs text-muted-foreground -mt-1 mb-4">
              {st.email}{st.mobile ? ` · ${st.mobile}` : ''}{st.college ? ` · ${st.college}` : ''}
              {st.inid ? ` · ${st.inid}` : ''} · Registered {fmtDate(st.registeredAt)}
            </p>

            {/* Quick stats */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <Stat icon={Trophy} label="INXP" value={(st.inxp || 0).toLocaleString()} />
              <Stat icon={GraduationCap} label="Level" value={st.level} />
              <Stat icon={Flame} label="Streak" value={`${st.streak || 0}d`} />
              <Stat icon={CheckCircle2} label="Activated" value={st.activated ? 'Yes' : 'No'} />
            </div>

            {/* Account flags */}
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground mb-3">
              <Badge variant="outline" className="text-[10px]">{st.passwordReset ? `Reset pwd ${fmtDate(st.passwordResetAt)}` : 'Never reset pwd'}</Badge>
              <Badge variant="outline" className="text-[10px]">Last active {fmtDate(st.lastActive)}</Badge>
            </div>

            {/* Self-enrolled intern: contact details and account actions belong to NexusAI, not the partner. */}
            {st.selfEnrolled && (
              <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-amber-700 dark:text-amber-400">{st.contactNote}</p>
            )}

            {/* Coupon code + temp password (reveal/hide) */}
            {!st.selfEnrolled && <div className="grid grid-cols-2 gap-2 mb-5">
              <SecretBox icon={Ticket} label="Coupon code" value={st.couponCode}
                shown={showCoupon} onToggle={() => setShowCoupon((v) => !v)} onCopy={copy} />
              <SecretBox icon={PwdIcon} label="Temp password" value={st.tempPassword} mono
                shown={showPwd} onToggle={() => setShowPwd((v) => !v)} onCopy={copy}
                hint="Active until they set a permanent password" />
            </div>}

            {/* Actions */}
            {onAction && !st.selfEnrolled && (
              <div className="flex items-center gap-2 flex-wrap mb-5">
                <Button variant="outline" size="sm" onClick={() => onAction.resend(st)}><Mail className="w-3.5 h-3.5 mr-1.5" /> Resend onboarding</Button>
                <Button variant="outline" size="sm" onClick={() => onAction.resetLink(st)}><KeyRound className="w-3.5 h-3.5 mr-1.5" /> Send reset link</Button>
                {st.revoked
                  ? <Button variant="outline" size="sm" className="text-emerald-500 border-emerald-500/30" onClick={() => onAction.reassign(st)}><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reassign</Button>
                  : <Button variant="outline" size="sm" className="text-rose-500 border-rose-500/30" onClick={() => onAction.revoke(st)}><Ban className="w-3.5 h-3.5 mr-1.5" /> Revoke access</Button>}
              </div>
            )}
            {st.revoked && st.revokedReason && (
              <p className="text-xs text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2 mb-5">Paused: {st.revokedReason}</p>
            )}

            {/* Application details — what the intern filled at TCI apply time (editable) */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><ClipboardList className="w-4 h-4 text-primary" /> Application details</h3>
              <div className="flex items-center gap-2">
                <button onClick={toggleHistory} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><History className="w-3.5 h-3.5" /> History</button>
                {!editing && <button onClick={startEdit} className="text-xs text-primary font-medium inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>}
              </div>
            </div>

            {/* Change history (audit trail) */}
            {showHistory && (
              <div className="rounded-xl border border-border p-3 mb-3 bg-muted/10">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Change history · managed audit log</p>
                {logs === null ? <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
                  : logs.length === 0 ? <p className="text-xs text-muted-foreground">No edits recorded yet.</p>
                  : (
                    <div className="space-y-2">
                      {logs.map((lg, i) => (
                        <div key={i} className="text-xs border-l-2 border-primary/40 pl-2.5">
                          <p className="text-[10px] text-muted-foreground">{fmtDateTime(lg.createdAt)} · {lg.changed_by_name || 'Partner'}</p>
                          {(lg.changes || []).map((c, j) => (
                            <p key={j} className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium">{c.label || c.field}:</span>
                              <span className="text-muted-foreground line-through">{c.old || '—'}</span>
                              <ArrowRight className="w-3 h-3 text-muted-foreground" />
                              <span className="text-foreground">{c.new || '—'}</span>
                            </p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}

            <div className="rounded-xl border border-border p-3 mb-5 bg-muted/20">
              {editing ? (
                <>
                  {/* Academic — cascading dynamic dropdowns (free text still allowed) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">University</p>
                      <Combobox value={form.university} onSelect={pickUniversity} icon={GraduationCap} loading={uniLoading}
                        options={universities.map((u) => ({ id: u.id, name: u.name, state: u.state }))}
                        placeholder="Select university" emptyHint="Type your university" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">College</p>
                      <Combobox value={form.college} onSelect={(c) => setField('college', c.name)} icon={Building2} loading={colLoading}
                        options={colleges.map((c) => ({ id: c.id, name: c.name }))}
                        placeholder={selectedUniId ? 'Select college' : 'Pick a university first (or type)'}
                        emptyHint={selectedUniId ? 'Type your college' : 'Select a university or type your own'} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Course stream</p>
                      <Combobox value={form.courseGroup} onSelect={pickStream} icon={Layers} loading={groupsLoading}
                        options={courseGroups.map((g) => ({ id: g.code, name: g.name }))}
                        placeholder="Select course stream" emptyHint="Type your course stream" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Degree / course</p>
                      <Combobox value={form.degree} onSelect={(c) => setField('degree', c.name)} icon={GraduationCap} loading={courseLoading}
                        options={courses.map((c) => ({ id: c.code, name: c.name }))}
                        placeholder={selectedGroupCode ? 'Select course' : 'Pick a stream first (or type)'}
                        emptyHint={selectedGroupCode ? 'No courses listed — type your own' : 'Select a stream to see its courses, or type your own'} />
                    </div>
                  </div>
                  {/* Everything else — plain inputs */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 mt-2.5">
                    {EDIT_FIELDS.filter(([k]) => !ACADEMIC_FIELDS.has(k)).map(([k, label]) => (
                      <div key={k}>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                        <Input value={form[k] ?? ''} onChange={(e) => setField(k, e.target.value)} className="h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                    <Button size="sm" onClick={saveEdits} disabled={saving}>{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save changes</Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Every change is recorded in the managed audit log and visible to the NexusAI admin.</p>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                    <AppField label="Full name" value={app.fullName} />
                    <AppField label="Email" value={app.email} />
                    <AppField label="Phone" value={app.phone} />
                    <AppField label="Father's name" value={app.fatherName} />
                    <AppField label="Mother's name" value={app.motherName} />
                    <AppField label="University" value={app.university} />
                    <AppField label="College" value={app.college} />
                    <AppField label="Course stream" value={app.courseGroup} />
                    <AppField label="Degree / course" value={app.degree || app.course} />
                    <AppField label="Session" value={app.session} />
                    <AppField label="Semester / year" value={app.semester || app.year} />
                    <AppField label="Graduation year" value={app.graduationYear} />
                    <AppField label="University reg. no." value={app.universityRegNo} />
                    <AppField label="Class roll no." value={app.classRollNo} />
                    <AppField label="University roll no." value={app.universityRollNo} />
                    <AppField label="Mode" value={app.mode} />
                    <AppField label="Availability" value={app.availability} />
                    <AppField label="Location" value={app.location} />
                    <AppField label="LinkedIn" value={app.linkedin} link />
                    <AppField label="GitHub" value={app.github} link />
                    <AppField label="Portfolio" value={app.portfolio} link />
                  </div>
                  {(app.experience || app.whyInterested) && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      {app.experience && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Experience</p><p className="text-xs">{app.experience}</p></div>}
                      {app.whyInterested && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Why interested</p><p className="text-xs">{app.whyInterested}</p></div>}
                    </div>
                  )}
                  {app.resumeUrl && (
                    <a href={app.resumeUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-primary font-medium">
                      <Download className="w-3.5 h-3.5" /> View résumé
                    </a>
                  )}
                </>
              )}
            </div>

            {/* Programs */}
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-primary" /> Programs enrolled ({programs.length})</h3>
            {programs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No program enrollments in this batch.</p>
            ) : (
              <div className="space-y-3">
                {programs.map((p, i) => <ProgramBlock key={p.programRef || i} p={p} batchId={batchId} studentId={studentId} onChanged={reload} />)}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ProgramBlock({ p, batchId, studentId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const m = p.metrics || {};
  const pc = p.programChange || {};

  const toggleProgramChange = async (enable) => {
    setBusy(true);
    try {
      await enableProgramChange(batchId, studentId, p.enrollmentId, enable);
      toast.success(enable ? 'Program change enabled for this intern' : 'Program change disabled');
      onChanged?.();
    } catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left p-3 hover:bg-muted/30">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="font-semibold text-sm truncate">{p.programName}</p>
          <Badge variant="outline" className="text-[10px] capitalize shrink-0">{p.status}</Badge>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
          <span>{m.lessons} lessons</span>
          <span>· Attend {m.attendancePct}%</span>
          <span>· Assign <b className={scoreCls(m.avgAssignmentScore)}>{m.assignmentsSubmitted ? `${m.avgAssignmentScore}%` : '—'}</b></span>
          <span>· Quiz {m.quizzesTaken ? `${m.quizzesPassed}/${m.quizzesTaken}` : '—'}</span>
          <span className="ml-auto text-primary">{open ? 'Hide' : 'Details'}</span>
        </div>
      </button>

      {open && (
        <div className="border-t border-border p-3 space-y-3 bg-muted/20">
          {/* Program change control */}
          <div className="flex items-center justify-between gap-2 flex-wrap rounded-lg border border-border bg-card p-2.5">
            <div className="text-xs">
              <p className="font-medium text-foreground flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5 text-primary" /> Program change</p>
              <p className="text-[11px] text-muted-foreground">
                {pc.used ? 'Used by the intern.' :
                  pc.canSwitch ? (pc.reEnabled ? `Re-enabled${pc.enabledByName ? ` by ${pc.enabledByName}` : ''}` : `${pc.daysLeft || 0} day(s) left in default window`) :
                  'Window closed — enable to let the intern switch program.'}
              </p>
            </div>
            {pc.canSwitch
              ? <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => toggleProgramChange(false)}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Disable'}</Button>
              : <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => toggleProgramChange(true)}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Enable change'}</Button>}
          </div>

          {/* Day-wise attendance */}
          {p.attendance && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Day-wise attendance · {p.attendance.attendedDays}/{p.attendance.totalDays} days · {p.attendance.pct}%</p>
              {(p.attendance.weekly || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {p.attendance.weekly.map((w) => {
                    const ratio = w.elapsed ? w.attended / w.elapsed : 0;
                    const cls = w.elapsed === 0 ? 'bg-muted text-muted-foreground border-border'
                      : ratio >= 0.75 ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25'
                      : ratio >= 0.4 ? 'bg-amber-500/10 text-amber-600 border-amber-500/25'
                      : 'bg-rose-500/10 text-rose-600 border-rose-500/25';
                    return <span key={w.week} className={cn('text-[10px] px-2 py-1 rounded-lg border', cls)} title={`Week ${w.week}: ${w.attended}/${w.elapsed || w.ofDays} days`}>W{w.week}: {w.attended}/{w.elapsed || w.ofDays}</span>;
                  })}
                </div>
              )}
            </div>
          )}

          {/* Assignments */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1"><FileText className="w-3 h-3" /> Assignment AI analysis</p>
            {p.assignments.length === 0 ? <p className="text-xs text-muted-foreground">No assignment submissions.</p> : (
              <div className="space-y-1.5">
                {p.assignments.map((a, i) => (
                  <div key={i} className="rounded-lg border border-border bg-card p-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">Week {a.week}</span>
                      <div className="flex items-center gap-2">
                        {a.verdict && <Badge variant="outline" className="text-[10px]">{a.verdict}</Badge>}
                        {typeof a.score === 'number' && <span className={cn('font-bold', scoreCls(a.score))}>{a.score}%</span>}
                        {a.passed === true ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : a.passed === false ? <XCircle className="w-3.5 h-3.5 text-rose-500" /> : <Clock className="w-3.5 h-3.5 text-muted-foreground/50" />}
                      </div>
                    </div>
                    {a.feedback && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-3">{a.feedback}</p>}
                    {Array.isArray(a.rubric) && a.rubric.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {a.rubric.map((rb, j) => <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{rb.criterion}: {rb.score}{rb.max ? `/${rb.max}` : ''}</span>)}
                      </div>
                    )}
                    {a.githubUrl && <a href={a.githubUrl} target="_blank" rel="noreferrer" className="text-[11px] text-primary inline-flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> Submission</a>}
                    {a.analysisStatus === 'pending' && <p className="text-[10px] text-amber-500 mt-1">AI analysis pending…</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quizzes */}
          {p.quizzes.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Quizzes</p>
              <div className="flex flex-wrap gap-1.5">
                {p.quizzes.map((q, i) => (
                  <span key={i} className={cn('text-[10px] px-2 py-1 rounded-lg border', q.passed ? 'border-emerald-500/25 text-emerald-500 bg-emerald-500/10' : 'border-rose-500/25 text-rose-500 bg-rose-500/10')}>
                    W{q.week}: {q.scorePct ?? '—'}%
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg bg-muted/40 p-2.5 text-center">
    <Icon className="w-3.5 h-3.5 text-primary mx-auto mb-1" />
    <p className="text-sm font-bold leading-none">{value ?? 0}</p>
    <p className="text-[10px] text-muted-foreground mt-1">{label}</p>
  </div>
);

const SecretBox = ({ icon: Icon, label, value, shown, onToggle, onCopy, mono, hint }) => (
  <div className="rounded-lg border border-border p-2.5 bg-muted/20">
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
      {value && (
        <div className="flex items-center gap-1.5">
          <button onClick={onToggle} title={shown ? 'Hide' : 'Reveal'} className="text-muted-foreground hover:text-foreground">{shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
          {shown && <button onClick={() => onCopy(value)} title="Copy" className="text-muted-foreground hover:text-foreground"><Copy className="w-3.5 h-3.5" /></button>}
        </div>
      )}
    </div>
    <p className={cn('text-sm mt-1', mono && 'font-mono', !value && 'text-muted-foreground/50')}>
      {value ? (shown ? value : '••••••••') : '—'}
    </p>
    {hint && value && <p className="text-[9px] text-muted-foreground/70 mt-0.5">{hint}</p>}
  </div>
);

const AppField = ({ label, value, link }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    {value
      ? (link
          ? <a href={String(value).startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-xs text-primary break-all hover:underline">{value}</a>
          : <p className="text-xs text-foreground break-words">{value}</p>)
      : <p className="text-xs text-muted-foreground/50">—</p>}
  </div>
);
